import { Link } from '@tanstack/react-router';
import { WorkflowIcon } from 'lucide-react';

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import type { WorkflowView } from '@/lib/api/generated/model';
import { boardEligibleWorkflows, useWorkflows } from '@/features/workflows';

/**
 * Pick one workflow in this workspace.
 *
 * Half of the watched-transition/target-workflow picker (research.md D3): a
 * watched transition needs one workflow plus *two* steps in it, which this
 * component alone cannot model — see {@link WorkflowStepSelect} for the step
 * half. Composed twice per `CreateTriggerProfileSheet` (watched workflow,
 * create-task target workflow), with no filter excluding one from the other
 * (nothing in the backend contract forbids targeting the same workflow a
 * profile watches).
 *
 * Published workflows only, same filter `boardEligibleWorkflows` applies: an
 * unpublished workflow has no committed steps to watch or target.
 */
export function WorkflowSelect({
  workspaceId,
  value,
  onChange,
  filter,
  disabled,
  'aria-label': ariaLabel,
}: {
  workspaceId: string;
  value: string | undefined;
  onChange: (workflowId: string) => void;
  filter?: (workflow: WorkflowView) => boolean;
  disabled?: boolean;
  'aria-label'?: string;
}) {
  const { workflows, isPending, loadFailed } = useWorkflows(workspaceId);
  const eligible = boardEligibleWorkflows(workflows).filter(
    (workflow) => filter?.(workflow) ?? true,
  );

  const placeholder = isPending
    ? 'Loading workflows…'
    : loadFailed
      ? 'Workflows could not be loaded'
      : eligible.length === 0
        ? 'No published workflows'
        : 'Select a workflow';

  // Nothing to pick, and nothing this form can do about it: a workflow is
  // published in the builder, not here (spec 124 FR-014). So the field says
  // so and points there — and the trigger form's draft (US2) is what keeps
  // the work while the person goes.
  if (!isPending && !loadFailed && eligible.length === 0 && !value) {
    return (
      <div className="border-input text-muted-foreground flex min-h-8 w-full flex-wrap items-center gap-x-2 rounded-lg border px-2.5 py-1 text-sm">
        <WorkflowIcon
          aria-hidden="true"
          className="size-4 shrink-0"
          strokeWidth={1.5}
        />
        <span>No published workflows yet</span>
        <Link
          to="/workspaces/$workspaceId/workflows"
          params={{ workspaceId }}
          className="text-primary focus-visible:ring-ring rounded-sm hover:underline focus-visible:ring-3 focus-visible:outline-none"
        >
          Open Workflows
        </Link>
      </div>
    );
  }

  return (
    <Select
      value={value}
      onValueChange={(next) => {
        if (next) onChange(next);
      }}
      disabled={disabled || isPending || loadFailed || eligible.length === 0}
    >
      <SelectTrigger aria-label={ariaLabel ?? 'Workflow'}>
        {/* A children function on `SelectValue` always wins over its
            `placeholder` prop in Base UI, regardless of whether a value is
            selected (`SelectValue.js`: the function branch is checked before
            the placeholder branch) — so the empty case has to be handled
            inside the function itself, not left to `placeholder`. */}
        <SelectValue>
          {(id: string | undefined) =>
            id
              ? (eligible.find((workflow) => workflow.id === id)?.name ?? id)
              : placeholder
          }
        </SelectValue>
      </SelectTrigger>
      <SelectContent>
        {eligible.map((workflow) => (
          <SelectItem key={workflow.id} value={workflow.id}>
            {workflow.name}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}
